import check from 'assets/img/check-solid-white.png';
import * as autobind from 'autobind';
import React, { Component, RefObject } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import NonMachineRelatedSubjectStepTw from './steps/non-machine-related-subject-step-tw';
import MachineRelatedSubjectStepTw from './steps/machine-related-subject-step-tw';
import BasicStepTw from './steps/basic-step-tw';
import {
  WizardDataBasicStepValidator,
  WizardDataNonMachineRelatedStepValidator,
  EventWizardData,
  NewEventsStore,
  CreateEventDto,
  WizardDataEventDetailsStepValidator,
  WizardDataMachineRelatedStepValidator,
  WizardDataEventInstructorsStepValidator,
  WizardDataDatesStepValidator,
  WizardDataEventStudentsStepValidator,
  CreateEventDetailsDto
} from 'stores/events/creation-wizard-store';
import { Message as ErrorMessage } from 'stores/types';
import { EventFieldGroups } from 'stores/configuration/events-workflow/event-types-store';
import { isNullOrWhiteSpaces, randomInteger, isNullOrEmpty } from 'utils/useful-functions';
import EventDetailsStepTw from './steps/event-details-step-tw';
import CommentsStepTw from './steps/comments-step-tw';
import InstructorsStepTw from './steps/instructors-step-tw';
import DatesStepTw from './steps/dates-step-tw';
import { connect } from 'redux-scaffolding-ts';
import { MachineListItemViewModel } from 'widgets/items-list/machine-list-item-deprecated';
import { RequestMachinesDto } from 'stores/requests/requests-store';
import ExtendedAbstractValidator from 'utils/extended-abstract-validator';
import TailwiStudentsAssignTableComponent from '../../shared/events-and-requests/tabs/students-assign-component-tw';
import { StudentsTabViewModel } from '../../shared/events-and-requests/tabs/students-assign-component';
import EventService, { EventDurationRequestDto } from 'stores/events/event-service';
import { resolve } from 'inversify.config';
import { ToastComponent } from 'site/pages/landing-pages/util/toast-component';

interface EventWizardMobileProps extends WithTranslation {
  onClose: (isSucess: boolean) => void;
  wizardStore?: NewEventsStore;
}

interface MobileStepDefinition {
  title: string;
  icon: any;
  validator: (wizardData: EventWizardData) => ExtendedAbstractValidator<EventWizardData>;
  visible: boolean;
  realIndx: number;
  clear: Partial<EventWizardData>;
}

interface MobileEventWizardState {
  currentStep: number;
  steps: MobileStepDefinition[];
  errorMessages: ErrorMessage[];
  wizardData: EventWizardData;
  warningMessages: string[];
  haveWarnings: boolean;
  isBusy: boolean;
  isLoading: boolean;
  durationCalculationWarning: boolean;
}

@connect(['wizardStore', NewEventsStore])
class MobileEventWizard extends Component<EventWizardMobileProps, MobileEventWizardState> {
  refModal: RefObject<any>;

  @resolve(EventService)
  eventsService: EventService;

  private getDurationRequestDto = (): EventDurationRequestDto => {
    const data = this.state.wizardData;
    return {
      isMachineRelated: data.isMachineRelated,
      mrRequest: data.isMachineRelated
        ? {
            originalEventTypeId: data.eventType?.originalEventTypeId,
            professionId: data.profession?.id,
            rows: (data.requestMachines || []).map(rm => ({
              clusterId: rm.cluster?.id,
              equipmentTypeId: rm.equipmentType?.id,
              machineModelId: rm.machineModel?.id,
              machineUnitsId: rm.machineUnits,
              oemId: rm.oem?.id
            })),
            trainingLevelId: data.trainingLevel?.id
          }
        : null,
      nmrRequest: !data.isMachineRelated
        ? {
            nmrClusterId: data.nmrCluster?.id ?? data.nmrClusterId,
            nmrFunctionalAreaId: data.nmrFunctionalArea?.id ?? data.nMRFunctionalAreaId,
            nmrTrainingNameId: data.nmrTrainingName?.id ?? data.nMRTrainingNameId
          }
        : null
    };
  };

  private isValidRequest = (dto: EventDurationRequestDto) => {
    if (dto == null) return false;
    if (dto.isMachineRelated) {
      if (
        dto.mrRequest == null ||
        isNullOrWhiteSpaces(dto.mrRequest.originalEventTypeId) ||
        isNullOrWhiteSpaces(dto.mrRequest.professionId) ||
        isNullOrWhiteSpaces(dto.mrRequest.trainingLevelId) ||
        (dto.mrRequest.rows || []).length === 0
      )
        return false;
      return dto.mrRequest.rows.all(r => !isNullOrWhiteSpaces(r.clusterId) && !isNullOrWhiteSpaces(r.equipmentTypeId));
    } else return !isNullOrWhiteSpaces(dto.nmrRequest?.nmrClusterId) && !isNullOrWhiteSpaces(dto.nmrRequest?.nmrFunctionalAreaId);
  };

  createEventDetails(): CreateEventDetailsDto {
    const { wizardData } = this.state;

    if (
      this.state.wizardData == null ||
      this.state.wizardData.eventType == null ||
      this.state.wizardData.eventType.requestFieldGroups == null ||
      this.state.wizardData.eventType.requestFieldGroups.indexOf('RequestDetails') < 0
    ) {
      return null;
    }

    if (
      this.state.wizardData?.eventType?.requestFieldGroups?.includes(EventFieldGroups[EventFieldGroups.RequestDetails]) &&
      this.state.wizardData?.eventType?.name.toLocaleLowerCase().includes('reserved') &&
      this.state.wizardData?.isMachineRelated == null &&
      this.state.wizardData?.category == null
    ) {
      return null;
    }

    return {
      isMachineRelated: wizardData.isMachineRelated,
      category: wizardData.category,
      locationId: wizardData.location ? wizardData.location.id : null,
      priorityId: wizardData.priority ? wizardData.priority.id : null,
      patternId: wizardData.pattern ? wizardData.pattern.id : null,
      professionId: wizardData.profession ? wizardData.profession.id : null,
      nmrClusterId: wizardData.nmrCluster && wizardData.nmrCluster.id,
      nmrFunctionalAreaId: wizardData.nmrFunctionalArea && wizardData.nmrFunctionalArea.id,
      nmrFunctionalSubAreaId: wizardData.nmrFunctionalSubArea && wizardData.nmrFunctionalSubArea.id,
      nmrTrainingNameId: wizardData.nmrTrainingName && wizardData.nmrTrainingName.id,
      requestedMachines: this.mapToRequestMachinesDto(wizardData.requestMachines)
    };
  }

  mapToRequestMachinesDto(machines: MachineListItemViewModel[]): RequestMachinesDto[] {
    if (!machines) return [];

    return machines.map(x => ({
      machineRelatedClusterId: x.cluster ? x.cluster.id : null,
      machineRelatedClusterName: x.cluster ? x.cluster.title : null,
      equipmentTypeId: x.equipmentType ? x.equipmentType.id : null,
      equipmentTypeName: x.equipmentType ? x.equipmentType.title : null,
      oemId: x.oem ? x.oem.id : null,
      oemName: x.oem ? x.oem.title : null,
      machineModelId: x.machineModel ? x.machineModel.id : null,
      machineModelName: x.machineModel ? x.machineModel.title : null,
      plcTypeRequestMachines: x.plcTypes,
      machineUnitRequestMachines: x.machineUnits
    }));
  }

  constructor(props: EventWizardMobileProps) {
    super(props);
    const { t } = props;
    this.state = {
      haveWarnings: false,
      currentStep: 0,
      isBusy: false,
      errorMessages: [],
      durationCalculationWarning: false,
      steps: [
        {
          title: t('Basic'),
          icon: undefined,
          validator: _ => new WizardDataBasicStepValidator(),
          visible: true,
          realIndx: 0,
          clear: {
            eventType: null,
            location: null,
            priority: null,
            category: null,
            profession: null,
            isMachineRelated: undefined
          }
        },
        {
          title: t('Subject'),
          icon: undefined,
          validator: (data: EventWizardData) =>
            data.isMachineRelated ? new WizardDataMachineRelatedStepValidator() : new WizardDataNonMachineRelatedStepValidator(),
          visible: false,
          realIndx: -1,
          clear: {
            usePattern: undefined,
            hasModifiedPattern: false,
            pattern: undefined,
            nmrClusterId: undefined,
            nMRFunctionalAreaId: undefined,
            nMRTrainingNameId: undefined,
            nMRFunctionalSubAreaId: undefined
          }
        },
        {
          title: t('Event Details'),
          icon: undefined,
          validator: _ => new WizardDataEventDetailsStepValidator(),
          visible: false,
          realIndx: -1,
          clear: {
            eventTitle: '',
            customizedData: undefined,
            deliveryMethod: undefined,
            language: undefined,
            trainingLevel: undefined,
            students: []
          }
        },
        {
          title: t('Dates'),
          icon: undefined,
          validator: _ => new WizardDataDatesStepValidator(),
          visible: true,
          realIndx: 1,
          clear: {
            eventDuration: '',
            dateFrom: undefined,
            dateTo: undefined,
            plannedDuration: ''
          }
        },
        {
          title: t('Students'),
          icon: undefined,
          validator: _ => new WizardDataEventStudentsStepValidator(),
          visible: false,
          realIndx: -1,
          clear: {}
        },
        {
          title: t('Instructors'),
          icon: undefined,
          validator: _ => new WizardDataEventInstructorsStepValidator(),
          visible: false,
          realIndx: -1,
          clear: {}
        },
        {
          title: t('Comments'),
          icon: undefined,
          validator: _ => undefined,
          visible: true,
          realIndx: 2,
          clear: {
            comments: ''
          }
        }
      ],
      wizardData: { students: [], numStudentsAssigned: 0, calculatedEventDuration: 0 } as EventWizardData,
      warningMessages: [],
      isLoading: false
    };
    this.props.wizardStore.createNew({} as CreateEventDto);
  }

  private mapToDto(wizardData: EventWizardData): CreateEventDto {
    return {
      locked: false,
      comments: wizardData.comments,
      startDate: wizardData.dateFrom,
      endDate: wizardData.dateTo,
      eventDuration: wizardData.eventDuration,
      eventTypeId: wizardData.eventType ? wizardData.eventType.id : '',
      eventDetails: this.createEventDetails(),
      plannedDuration: wizardData.plannedDuration,
      title: wizardData.eventTitle || this.createEventTitle(),
      eventTrainingDetails: this.createEventTrainingDetails(),
      parentRequestId: wizardData.parentRequestId,
      trainingMaterials: wizardData.trainingMaterials,
      instructors: wizardData.instructors,
      travelDays: wizardData.travelDays,
      pausePeriods: wizardData.pausePeriods,
      eventWarnings: wizardData.eventWarnings,
      students: wizardData.students,
      numStudentsAssigned: wizardData.numStudentsAssigned
    };
  }

  @autobind
  private onCreateNewItem() {
    const { wizardData } = this.state;
    const dto = this.mapToDto(wizardData);
    if (dto.instructors && dto.instructors.length > 0) {
      dto.instructors.forEach(x => {
        x.period.from = new Date(x.period.from).toUTCString();
        x.period.to = new Date(x.period.to).toUTCString();
      });
    }
    this.props.wizardStore.change(dto);

    this.props.wizardStore
      .submit()
      .then(res => {
        if (res.isSuccess) {
          ToastComponent({ text: this.props.t('Send Event successfully!'), type: 'success-toast' });
          this.props.onClose(true);
        } else if (this.props.wizardStore.state.result?.messages) {
          this.props.wizardStore.state.result.messages.forEach(msg => ToastComponent({ text: msg.body, type: 'warning-toast' }));
        }
      })
      .catch(err => {
        console.error(err);
        ToastComponent({ text: this.props.t('Event save failed'), type: 'warning-toast' });
      });
  }

  @autobind
  private onCancelNewItem() {
    this.props.wizardStore.clear();
    this.setState({ wizardData: { students: [] } as EventWizardData });
    this.props.onClose(false);
  }

  private createEventTitle() {
    return `Event_${randomInteger(100, 1000000)}`;
  }

  private createEventTrainingDetails() {
    const { wizardData } = this.state;

    if (
      this.state.wizardData == null ||
      this.state.wizardData.eventType == null ||
      this.state.wizardData.eventType.requestFieldGroups == null ||
      this.state.wizardData.eventType.requestFieldGroups.indexOf('EventDetails') < 0
    ) {
      return {
        customizationDataId: null,
        languageId: null,
        deliveryMethodId: null,
        trainingLevelId: null,
        assignedStudentIds: wizardData.students ? wizardData.students.map(student => student.id) : [],
        numStudentsAssigned: wizardData.numStudentsAssigned
      };
    }
    return {
      customizationDataId: wizardData.customizedData ? wizardData.customizedData.id : null,
      languageId: wizardData.language ? wizardData.language.id : null,
      deliveryMethodId: wizardData.deliveryMethod ? wizardData.deliveryMethod.id : null,
      trainingLevelId: wizardData.trainingLevel ? wizardData.trainingLevel.id : null,
      assignedStudentIds: wizardData.students ? wizardData.students.map(student => student.id) : [],
      numStudentsAssigned: wizardData.numStudentsAssigned
    };
  }

  private getStep1Visibility(wizard: EventWizardData) {
    let visible = wizard?.eventType?.requestFieldGroups?.includes(EventFieldGroups[EventFieldGroups.RequestDetails]);
    if (visible && wizard?.eventType?.name.toLocaleLowerCase().includes('reserved')) {
      visible = visible && (wizard?.isMachineRelated != null || wizard?.category != null);
    }
    return visible;
  }

  private updateSteps = (steps: MobileStepDefinition[], wizardData: EventWizardData) => {
    steps[1].visible = this.getStep1Visibility(wizardData);
    steps[2].visible = wizardData?.eventType?.requestFieldGroups?.includes(EventFieldGroups[EventFieldGroups.EventDetails]);
    steps[4].visible = wizardData && wizardData.eventType && wizardData.eventType.participants;
    steps[5].visible = !isNullOrWhiteSpaces(wizardData?.eventType?.instructor) && wizardData?.eventType?.instructor !== 'No';
    steps
      .filter(s => s.visible)
      .forEach((s, idx) => {
        s.realIndx = idx;
      });
    return steps;
  };

  private onValueChanged = (...changes: [string, any][]) => {
    const wizardData = { ...this.state.wizardData };
    const { currentStep } = this.state;
    (changes || []).forEach(([prop, value]) => {
      if (
        wizardData?.eventType?.requestFieldGroups?.includes(EventFieldGroups[EventFieldGroups.RequestDetails]) &&
        wizardData?.eventType?.name.toLocaleLowerCase().includes('reserved') &&
        currentStep === 0 &&
        (prop === 'category' || prop === 'isMachineRelated')
      ) {
        wizardData['category'] = wizardData['category'] ? wizardData['category'] : 20;
        wizardData['isMachineRelated'] = wizardData['isMachineRelated'] ? wizardData['isMachineRelated'] : false;
      }
      wizardData[prop] = value;
    });
    this.setState({ wizardData }, () => ({ steps: this.updateSteps(this.state.steps, wizardData) }));
  };

  private onValueChangedInstructors = (...changes: [string, any][]) => {
    let notInDate: boolean = false;
    let notValid: boolean = false;
    let notConflict: boolean = false;
    let haveWarnings: boolean = false;
    let warningMessages: string[] = [];

    const wizardData = { ...this.state.wizardData };
    changes.forEach(([prop, value]) => {
      wizardData[prop] = value;
    });
    if (wizardData.instructors.length === 0) this.setState({ haveWarnings: false, warningMessages: [] });
    else {
      for (let i = 0; i < wizardData.instructors.length; i++) {
        if (wizardData.instructors[i].notInDate !== undefined && wizardData.instructors[i].notInDate === true) notInDate = true;
        if (wizardData.instructors[i].notValid !== undefined && wizardData.instructors[i].notValid === true) notValid = true;
        if (wizardData.instructors[i].notConflict !== undefined && wizardData.instructors[i].notConflict === true) notConflict = true;
      }
    }

    if (notInDate === true) {
      warningMessages.push('There is a conflict with the Dates');
      haveWarnings = true;
    }

    if (notValid === true) {
      warningMessages.push("There is a conflict with the Instructor's Expertise");
      haveWarnings = true;
    }

    if (notConflict === true) {
      warningMessages.push("There is a conflict with others Instructor's Events");
      haveWarnings = true;
    }

    const createWarnings = warningMessages.map(key => {
      return {
        description: key
      };
    });

    wizardData.eventWarnings = createWarnings;

    this.setState({ wizardData }, () => ({ steps: this.updateSteps(this.state.steps, wizardData), warningMessages, haveWarnings }));
  };

  @autobind
  private handleNext() {
    const { steps, currentStep, wizardData } = this.state;
    let { errorMessages } = this.state;

    let nextStep = currentStep + 1;
    while (nextStep < steps.length && !steps[nextStep].visible) nextStep++;

    if (nextStep >= steps.length) return;

    const validator: ExtendedAbstractValidator<EventWizardData> = steps[currentStep].validator(wizardData);
    if (validator != null) {
      const result = validator.extendValidate(wizardData);
      errorMessages = result.isValid() ? [] : result.getFailureMessages().map(x => ({ level: 'Error', body: x }));
    }

    if (errorMessages.length !== 0) {
      this.setState({ errorMessages });
    } else {
      const updatedSteps = this.updateSteps(steps, wizardData);
      updatedSteps.forEach((s, idx) => {
        if (idx <= currentStep) {
          s.icon = check;
        }
      });

      if (steps[nextStep].title === 'Dates') {
        const dto = this.getDurationRequestDto();
        const isValid = this.isValidRequest(dto);
        if (!isValid) {
          this.setState({ currentStep: nextStep, steps: updatedSteps, errorMessages, durationCalculationWarning: true });
        } else {
          this.setState({ isBusy: true });
          this.eventsService
            .calculateEventDuration(dto)
            .then(v => {
              const w = { ...this.state.wizardData };
              w.calculatedEventDuration = v.days;
              if (isNullOrEmpty(w.plannedDuration) || w.plannedDuration === '0 w/d' || w.plannedDuration === '0') {
                w.plannedDuration = `${v.days}`;
                w.userEventDuration = v.days;
              }
              this.setState({
                currentStep: nextStep,
                steps: updatedSteps,
                errorMessages,
                durationCalculationWarning: false,
                isBusy: false,
                wizardData: { ...w }
              });
            })
            .catch(_ => {
              const w = { ...this.state.wizardData };
              w.calculatedEventDuration = 0;
              if (isNullOrEmpty(w.plannedDuration)) {
                w.plannedDuration = '0';
                w.userEventDuration = 0;
              }
              this.setState({
                currentStep: nextStep,
                steps: updatedSteps,
                errorMessages,
                durationCalculationWarning: true,
                isBusy: false,
                wizardData: { ...w }
              });
            });
        }
      } else {
        this.setState({ currentStep: nextStep, steps: updatedSteps, errorMessages });
      }
    }
  }

  private stepBack = () => {
    const { steps, currentStep } = this.state;

    let nextStep = currentStep - 1;
    while (nextStep >= 0 && !steps[nextStep].visible) {
      steps[nextStep].icon = null;
      nextStep--;
    }

    if (nextStep < 0) return;

    steps[nextStep].icon = null;
    this.setState({ currentStep: nextStep, steps });
  };

  private getStepContent = () => {
    const { wizardData, currentStep } = this.state;
    switch (currentStep) {
      case 0:
        return (
          <BasicStepTw
            wizardData={wizardData}
            onValueChanged={this.onValueChanged}
            recalculateEventDuration={() => {
              const dto = this.getDurationRequestDto();
              const isValid = this.isValidRequest(dto);
              if (!isValid) return Promise.reject();
              return new Promise(() => {
                this.setState({ isBusy: true });
                this.eventsService
                  .calculateEventDuration(dto)
                  .then(v => {
                    const w = { ...this.state.wizardData };
                    w.calculatedEventDuration = v.days;
                    if (isNullOrEmpty(w.plannedDuration) || w.plannedDuration === '0 w/d' || w.plannedDuration === '0') {
                      w.plannedDuration = `${v.days}`;
                      w.userEventDuration = v.days;
                    }
                    this.setState({ durationCalculationWarning: false, isBusy: false, wizardData: { ...w } });
                  })
                  .catch(_ => {
                    const w = { ...this.state.wizardData };
                    w.calculatedEventDuration = 0;
                    if (isNullOrEmpty(w.plannedDuration)) {
                      w.plannedDuration = '0';
                      w.userEventDuration = 0;
                    }
                    this.setState({ isBusy: false, durationCalculationWarning: true, wizardData: { ...w } });
                  });
              });
            }}
          />
        );
      case 1:
        return wizardData.isMachineRelated ? (
          <MachineRelatedSubjectStepTw wizardData={wizardData} onValueChanged={this.onValueChanged} />
        ) : (
          <NonMachineRelatedSubjectStepTw wizardData={wizardData} onValueChanged={this.onValueChanged} />
        );
      case 2:
        return <EventDetailsStepTw wizardData={wizardData} onValueChanged={this.onValueChanged} />;
      case 3:
        return (
          <DatesStepTw
            durationWarning={this.state.durationCalculationWarning}
            wizardData={wizardData}
            onValueChanged={this.onValueChanged}
          />
        );
      case 4:
        return this.createStudentsComponent();
      case 5:
        return (
          <InstructorsStepTw
            wizardData={wizardData}
            onValueChanged={this.onValueChangedInstructors}
            instructorBusy={this.handleInstructorBusy}
          />
        );
      case 6:
        return <CommentsStepTw wizardData={wizardData} onValueChanged={this.onValueChanged} />;
      default:
        return null;
    }
  };

  handleInstructorBusy = (busy: boolean) => {
    this.setState({ isLoading: busy });
  };

  createStudentsComponent = (): React.ReactNode => {
    const { students, numStudentsAssigned } = this.state.wizardData;
    const value: StudentsTabViewModel = { students, studentsAssigned: numStudentsAssigned };

    return (
      <TailwiStudentsAssignTableComponent
        className="wizard__students-step__container"
        readOnly={false}
        value={value}
        onChange={this.handleOnStudentsChange}
        cameFrom="Event"
      />
    );
  };

  private handleOnStudentsChange = (newValue: StudentsTabViewModel) => {
    const { wizardData } = this.state;
    wizardData.students = newValue.students;
    wizardData.numStudentsAssigned = newValue.studentsAssigned;
    this.setState({ wizardData }, () => {
      const { steps } = this.state;
      this.setState({ steps: this.updateSteps(steps, wizardData) });
    });
  };

  public render() {
    const { t, wizardStore } = this.props;
    const { currentStep, steps, errorMessages, warningMessages, haveWarnings, isLoading } = this.state;
    const { result } = wizardStore.state;
    // const visibleSteps = steps.filter(s => s.visible);

    return (
      <div className="p-4 relative" style={{ paddingBottom: '120px' /* espacio para toolbar */ }}>
        <button onClick={this.onCancelNewItem} className="flex items-center gap-1 text-sm text-black-600 mb-2" type="button">
          ← {t('Close')}
        </button>

        <div className="flex items-center mb-4">
          <span className="text-lg font-semibold flex-1">
            <h3>{t('Event Creation.')}</h3>
          </span>
          <span className="flex flex-1 justify-center items-center">
            <span className="font-semibold mr-2">{this.state.steps[currentStep].title}</span>
          </span>
        </div>

        {(this.state.isBusy || this.props.wizardStore?.state.isBusy) && (
          <div className="fixed inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50">
            <span className="sr-only">{t('Loading')}</span>
            <svg className="animate-spin h-10 w-10 text-primary-600" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          </div>
        )}

        {!!errorMessages.length && (
          <section role="alert" className="bg-red-50 border border-red-300 p-4 rounded mb-6 text-red-700">
            <strong>{t('An error occurred')}</strong>
            <ul className="list-disc list-inside mt-2">
              {errorMessages.map((e, i) => (
                <li key={`err-${i}`}>{e.body}</li>
              ))}
            </ul>
          </section>
        )}

        {result && !result.isSuccess && (
          <section role="alert" className="bg-red-50 border border-red-300 p-4 rounded mb-6 text-red-700">
            <strong>{t('An error occurred')}</strong>
            <ul className="list-disc list-inside mt-2">
              {result.messages.map((m, i) => (
                <li key={`resmsg-${i}`}>{m.body}</li>
              ))}
            </ul>
          </section>
        )}

        {this.getStepContent()}

        {haveWarnings && (
          <div className="bg-yellow-50 border border-yellow-300 rounded p-4 mt-6 flex items-start gap-3 text-yellow-800" role="alert">
            <svg
              className="w-6 h-6 flex-shrink-0 text-yellow-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.29 11.29l.083.094a.583.583 0 00.837.083l.094-.083 3-3a.6.6 0 00-.707-.707L12 9.586 9.707 7.293a.6.6 0 00-.707.707l3 3"
              />
              <circle cx={12} cy={17} r={1} fill="currentColor" />
            </svg>
            <div>
              {warningMessages.map((msg, i) => (
                <p key={`warn-${i}`} className="text-sm">
                  {msg}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Fixed Bottom Toolbar */}
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 58,
            right: 0,
            background: 'white',
            borderTop: '1px solid #ddd',
            padding: '0.5rem 1rem',
            zIndex: 1000
          }}
          className="flex gap-2"
        >
          {currentStep > 0 && (
            <button
              type="button"
              onClick={this.stepBack}
              disabled={currentStep === 0}
              className="flex-1 bg-primary-600 text-white py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('Back')}
            </button>
          )}

          <button type="button" onClick={this.onCancelNewItem} className="flex-1 bg-primary-600 text-white py-2 rounded">
            {t('Cancel')}
          </button>
          {currentStep === steps.length - 1 ? (
            <button
              type="button"
              disabled={isLoading}
              onClick={this.onCreateNewItem}
              className={`flex-1 ${isLoading ? 'bg-gray-400' : 'bg-primary-600'} text-white py-2 rounded font-semibold`}
            >
              {t('Save')}
            </button>
          ) : (
            <button
              type="button"
              disabled={isLoading}
              onClick={this.handleNext}
              className={`flex-1 ${isLoading ? 'bg-gray-400' : 'bg-primary-600'} text-white py-2 rounded`}
            >
              {t('Next')}
            </button>
          )}
        </div>
      </div>
    );
  }
}

export default withTranslation()(MobileEventWizard);
